import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { WmsBatchProgress, WmsBatchResult, WmsDataService } from '@luzula/luzula-lib';
import { AufnahmePunktDocument } from '../../data-model';

type Phase = 'optionen' | 'lauft' | 'fertig';

interface LogEntry {
	pointId: string;
	status: 'ok' | 'error' | 'skipped';
	reason?: string;
}

/**
 * Modal zum sequentiellen Abruf von WMS-Daten für eine Liste bereits ausgewählter
 * Punkte. Wird vom Batch-Edit-Wizard via Auswahl-Step gestartet (Sackgasse — nach
 * dem Lauf wird der ursprüngliche Wizard nicht erneut betreten).
 *
 * Bewusst konservativ: sequentielle Verarbeitung mit Pause zwischen Punkten,
 * Abbruch jederzeit möglich, um nicht versehentlich eine IP-Sperre auf dem
 * Gateway oder den externen WMS-Servern auszulösen.
 */
@Component({
	selector: 'bp-batch-wms-progress',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbProgressbarModule],
	templateUrl: './batch-wms-progress.component.html',
	styleUrls: ['./batch-wms-progress.component.css']
})
export class BatchWmsProgressComponent implements OnInit {
	/** Punkte, für die WMS-Daten neu abgerufen werden sollen. */
	@Input() points: AufnahmePunktDocument[] = [];

	private wms = inject(WmsDataService);
	private modal = inject(NgbActiveModal);

	phase: Phase = 'optionen';

	// Optionen
	overwrite = false;
	delayMsBetweenPoints = 1000;

	// Live-Status
	progressDone = 0;
	progressTotal = 0;
	currentPointId = '';
	counts = { ok: 0, errors: 0, skipped: 0 };
	log: LogEntry[] = [];

	private abortCtrl?: AbortController;
	result: WmsBatchResult | null = null;

	ngOnInit(): void {
		this.progressTotal = this.points.length;
	}

	async start(): Promise<void> {
		if (this.points.length === 0) {
			this.modal.dismiss('empty');
			return;
		}
		this.phase = 'lauft';
		this.abortCtrl = new AbortController();
		this.counts = { ok: 0, errors: 0, skipped: 0 };
		this.log = [];

		this.result = await this.wms.fetchWmsDataForDocumentsSequential(this.points, {
			overwrite: this.overwrite,
			delayMsBetweenPoints: this.delayMsBetweenPoints,
			signal: this.abortCtrl.signal,
			onProgress: (p: WmsBatchProgress) => this.onProgress(p)
		});

		this.phase = 'fertig';
	}

	private onProgress(p: WmsBatchProgress): void {
		this.progressDone = p.done;
		this.progressTotal = p.total;
		this.currentPointId = p.pointId;
		this.counts[p.status === 'ok' ? 'ok' : p.status === 'error' ? 'errors' : 'skipped']++;
		// Nur auffällige Einträge ins Log (Errors + Skips). Erfolgreiche Punkte
		// würden bei vielen Punkten den DOM zumüllen.
		if (p.status !== 'ok') {
			this.log.push({ pointId: p.pointId, status: p.status, reason: p.reason });
		}
	}

	abort(): void {
		this.abortCtrl?.abort();
	}

	close(): void {
		this.modal.close(this.result);
	}

	cancel(): void {
		this.modal.dismiss('cancel');
	}

	get progressPercent(): number {
		return this.progressTotal ? (this.progressDone / this.progressTotal) * 100 : 0;
	}
}
